import React from 'react';
import { View as RNView, Pressable as RNPressable } from 'react-native-web';
import { useNavigate } from 'react-router-dom';
import CustomText from '../components/atoms/CustomText';
import { colors, spacing, typography, borderRadius, shadows } from '../utils/styling';

// Type-safe components
const View = RNView as unknown as React.FC<React.ComponentProps<typeof RNView>>;
const Pressable = RNPressable as unknown as React.FC<React.ComponentProps<typeof RNPressable>>;

export default function SubscriptionRequired() {
  const navigate = useNavigate();

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.card}>
          <View style={styles.iconContainer}>
            <CustomText style={styles.icon}>🔒</CustomText>
          </View>
          
          <CustomText style={styles.title}>
            Subscription Required
          </CustomText>
          
          <CustomText style={styles.subtitle}>
            Access to the client portal requires an active subscription
          </CustomText>
          
          <CustomText style={styles.description}>
            To view your personalized money personality results, exercises, and manage your subscription, 
            you'll need to have an active subscription to our service.
          </CustomText>
          
          <View style={styles.features}>
            <CustomText style={styles.featuresTitle}>
              What's included with your subscription:
            </CustomText>
            <View style={styles.featureList}>
              <View style={styles.featureItem}>
                <CustomText style={styles.featureIcon}>✅</CustomText>
                <CustomText style={styles.featureText}>Detailed personality analysis</CustomText>
              </View>
              <View style={styles.featureItem}>
                <CustomText style={styles.featureIcon}>✅</CustomText>
                <CustomText style={styles.featureText}>Personalized exercises and recommendations</CustomText>
              </View>
              <View style={styles.featureItem}>
                <CustomText style={styles.featureIcon}>✅</CustomText>
                <CustomText style={styles.featureText}>Subscription management portal</CustomText>
              </View>
              <View style={styles.featureItem}>
                <CustomText style={styles.featureIcon}>✅</CustomText>
                <CustomText style={styles.featureText}>Ongoing access to your results</CustomText>
              </View>
            </View>
          </View>
          
          <View style={styles.actions}>
            <Pressable
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: hovered ? colors.primary : colors.secondary,
                paddingVertical: spacing.lg,
                paddingHorizontal: spacing.xl,
                borderRadius: borderRadius.md,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: spacing.md,
              })}
              onPress={() => navigate('/')}
            >
              <CustomText style={styles.primaryButtonText}>
                Get Started with Subscription
              </CustomText>
            </Pressable>
            
            <Pressable
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: hovered ? colors.gray[200] : colors.gray[100],
                paddingVertical: spacing.md,
                paddingHorizontal: spacing.xl,
                borderRadius: borderRadius.md,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              })}
              onPress={() => navigate('/login')}
            >
              <CustomText style={styles.secondaryButtonText}>
                Back to Login
              </CustomText>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.gray[100],
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  content: {
    maxWidth: 600,
    width: '100%',
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.xl,
    padding: spacing.xl,
    boxShadow: shadows.lg,
    textAlign: 'center',
  },
  iconContainer: {
    marginBottom: spacing.lg,
  },
  icon: {
    fontSize: 64,
    marginBottom: spacing.md,
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    color: colors.dark,
    marginBottom: spacing.md,
    fontFamily: "Nunito",
  },
  subtitle: {
    fontSize: 18,
    color: colors.gray[600],
    marginBottom: spacing.lg,
    fontFamily: "Nunito",
  },
  description: {
    fontSize: 16,
    color: colors.gray[700],
    marginBottom: spacing.xl,
    fontFamily: "Nunito",
  },
  features: {
    marginBottom: spacing.xl,
  },
  featuresTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.dark,
    marginBottom: spacing.md,
    fontFamily: "Nunito",
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  featureItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: spacing.lg,
  },
  featureIcon: {
    fontSize: 16,
    marginRight: spacing.sm,
    width: 20,
  },
  featureText: {
    fontSize: 14,
    color: colors.gray[700],
    fontFamily: "Nunito",
  },
  actions: {
    marginBottom: spacing.lg,
  },
  primaryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    fontFamily: "Nunito",
  },
  secondaryButtonText: {
    color: colors.gray[700],
    fontSize: 14,
    fontWeight: '500',
    fontFamily: "Nunito",
  },
  contact: {
    borderTop: `1px solid ${colors.gray[200]}`,
    paddingTop: spacing.lg,
  },
  contactText: {
    fontSize: 14,
    color: colors.gray[600],
    fontFamily: "Nunito",
  },
  contactLink: {
    color: colors.primary,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
} as const; 